// =============================================================================
// Realtime - Connessione socket.io
// =============================================================================
// Una sola connessione per scheda, condivisa da tutta l'applicazione: il
// gestionale apre gia' molte query HTTP, aprire anche piu' socket sarebbe uno
// spreco e moltiplicherebbe gli eventi ricevuti.
//
// La riconnessione la gestisce socket.io (backoff esponenziale). Chi ascolta
// deve pero' sapere quando e' avvenuta: durante la disconnessione gli eventi
// sono andati persi, e i dati a schermo possono essere fermi a minuti fa.
// =============================================================================

import { io, Socket } from 'socket.io-client';

/**
 * Il socket vive sotto il prefisso dell'API invece che sulla root: cosi' passa
 * dallo stesso proxy che gia' instrada `/api` (Vite in sviluppo, reverse proxy
 * in produzione) senza una regola dedicata.
 */
const SOCKET_PATH = '/api/v1/socket.io';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/** Origin su cui aprire il socket, dedotto dall'URL dell'API. */
function socketOrigin(): string {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

let socket: Socket | null = null;
let currentToken: string | null = null;

/**
 * Restituisce il socket connesso per il token indicato, creandolo se serve.
 * Un token diverso (nuovo login) chiude la connessione precedente: le
 * credenziali viaggiano nell'handshake, quindi vanno rinegoziate.
 */
export function getRealtimeSocket(token: string): Socket {
  if (socket && currentToken === token) {
    return socket;
  }

  closeRealtimeSocket();

  currentToken = token;
  socket = io(socketOrigin(), {
    path: SOCKET_PATH,
    // Il token nell'handshake, non in querystring: gli URL finiscono nei log
    // di proxy e server.
    auth: { token },
    withCredentials: true,
    // Si parte da WebSocket; il polling resta come ripiego per le reti che
    // bloccano l'Upgrade (proxy aziendali).
    transports: ['websocket', 'polling'],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });

  return socket;
}

export function closeRealtimeSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  currentToken = null;
}
